import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Calendar, Mail, Phone, MapPin, User, Clock, Shield, FileText, DollarSign, ChevronDown, ChevronRight, Download, Trash2, AlertTriangle, Info, List } from 'lucide-react';
import { supabase, getAvailableEvents, getLineup, subscribeToLineup, type EventAvailability, type LineupContestant } from '../lib/supabase';
import Button from '../components/Button';
import Select from '../components/Select';
import EventAvailabilityModal from '../components/EventAvailabilityModal';

interface ContestantData {
  id: string;
  event_id: string;
  event_name: string;
  event_type: string;
  price: number;
  first_name: string;
  last_name: string;
  email: string;
  address: string;
  postal_code: string;
  phone_number: string;
  birth_date: string;
  gender: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  emergency_contact_relation: string;
  age_verification: boolean;
  waiver_required?: boolean;
  interesting_fact: string;
  full_signature: string;
  waiver_accepted: boolean;
  payment_status: string;
  confirmation_number?: string;
  created_at: string;
  partner_name?: string;
  partner_email?: string;
  partner_phone?: string;
  team_entry_id?: string;
  is_team_event?: boolean;
}

// Mock data for when Supabase is not available
const mockContestantData: ContestantData[] = [
  // July 3rd contestants
  {
    id: '1', event_id: '1', event_name: 'Bull Riding (at beginning of rodeo)', event_type: 'July 3rd', price: 100,
    first_name: 'Jake', last_name: 'Williams', email: 'jake.williams@email.com', address: '123 Ranch Rd', postal_code: '84654',
    phone_number: '4355551234', birth_date: '1995-03-15', gender: 'Male', emergency_contact_name: 'Sarah Williams',
    emergency_contact_phone: '4355555678', emergency_contact_relation: 'Wife', age_verification: true,
    interesting_fact: '3-time state champion bull rider', full_signature: 'Jake Williams', waiver_accepted: true,
    payment_status: 'pending', confirmation_number: '1234567', created_at: '2026-01-15T10:30:00Z'
  },
  {
    id: '2', event_id: '2', event_name: 'Tie Down Roping', event_type: 'July 3rd', price: 85,
    first_name: 'Marcus', last_name: 'Rodriguez', email: 'marcus.r@email.com', address: '456 Valley St', postal_code: '84655',
    phone_number: '4355559876', birth_date: '1988-07-22', gender: 'Male', emergency_contact_name: 'Maria Rodriguez',
    emergency_contact_phone: '4355554321', emergency_contact_relation: 'Mother', age_verification: true,
    interesting_fact: 'Trains horses in the off-season', full_signature: 'Marcus Rodriguez', waiver_accepted: true,
    payment_status: 'pending', confirmation_number: '2345678', created_at: '2026-01-16T14:15:00Z'
  },
  {
    id: '3', event_id: '8', event_name: 'Team Roping', event_type: 'July 3rd', price: 85,
    first_name: 'Connor', last_name: 'Smith', email: 'connor.smith@email.com', address: '789 Rodeo Ave', postal_code: '84656',
    phone_number: '4355557777', birth_date: '1992-11-08', gender: 'Male', emergency_contact_name: 'Linda Smith',
    emergency_contact_phone: '4355558888', emergency_contact_relation: 'Mother', age_verification: true,
    interesting_fact: 'Been team roping since age 10', full_signature: 'Connor Smith', waiver_accepted: true,
    payment_status: 'pending', confirmation_number: '3456789', created_at: '2026-01-17T09:45:00Z',
    partner_name: 'Tyler Johnson', partner_email: 'tyler.j@email.com', partner_phone: '4355559999',
    team_entry_id: 'team-1', is_team_event: true
  },
  {
    id: '4', event_id: '8', event_name: 'Team Roping', event_type: 'July 3rd', price: 85,
    first_name: 'Tyler', last_name: 'Johnson', email: 'tyler.j@email.com', address: '321 Horse Trail', postal_code: '84657',
    phone_number: '4355559999', birth_date: '1990-05-14', gender: 'Male', emergency_contact_name: 'Connor Smith',
    emergency_contact_phone: '4355557777', emergency_contact_relation: 'Team Partner', age_verification: true,
    interesting_fact: 'Team partner of Connor Smith', full_signature: 'Tyler Johnson', waiver_accepted: true,
    payment_status: 'pending', confirmation_number: '3456789', created_at: '2026-01-17T09:45:00Z',
    partner_name: 'Connor Smith', partner_email: 'connor.smith@email.com', partner_phone: '4355557777',
    team_entry_id: 'team-1', is_team_event: true
  },
  {
    id: '5', event_id: '9', event_name: 'Barrel Racing', event_type: 'July 3rd', price: 85,
    first_name: 'Emma', last_name: 'Thompson', email: 'emma.thompson@email.com', address: '654 Prairie Ln', postal_code: '84658',
    phone_number: '4355552222', birth_date: '1997-09-03', gender: 'Female', emergency_contact_name: 'Robert Thompson',
    emergency_contact_phone: '4355553333', emergency_contact_relation: 'Father', age_verification: true,
    interesting_fact: 'Youngest rider to qualify for nationals', full_signature: 'Emma Thompson', waiver_accepted: true,
    payment_status: 'pending', confirmation_number: '4567890', created_at: '2026-01-18T16:20:00Z'
  },
  
  // July 4th contestants
  {
    id: '6', event_id: '13', event_name: 'Tie Down Roping', event_type: 'July 4th', price: 85,
    first_name: 'David', last_name: 'Anderson', email: 'david.anderson@email.com', address: '987 Canyon Rd', postal_code: '84659',
    phone_number: '4355554444', birth_date: '1985-12-25', gender: 'Male', emergency_contact_name: 'Jennifer Anderson',
    emergency_contact_phone: '4355555555', emergency_contact_relation: 'Wife', age_verification: true,
    interesting_fact: 'Christmas baby who loves rodeos', full_signature: 'David Anderson', waiver_accepted: true,
    payment_status: 'pending', confirmation_number: '5678901', created_at: '2026-01-19T11:10:00Z'
  },
  {
    id: '7', event_id: '20', event_name: 'Barrel Racing', event_type: 'July 4th', price: 85,
    first_name: 'Sophia', last_name: 'Martinez', email: 'sophia.martinez@email.com', address: '147 Mesa Dr', postal_code: '84660',
    phone_number: '4355556666', birth_date: '1994-04-17', gender: 'Female', emergency_contact_name: 'Carlos Martinez',
    emergency_contact_phone: '4355557777', emergency_contact_relation: 'Father', age_verification: true,
    interesting_fact: 'Trains rescue horses in spare time', full_signature: 'Sophia Martinez', waiver_accepted: true,
    payment_status: 'pending', confirmation_number: '6789012', created_at: '2026-01-20T13:30:00Z'
  },

  // Slack Rodeo 7AM contestants
  {
    id: '8', event_id: '23', event_name: 'Tie Down Roping', event_type: 'Slack Rodeo 7AM', price: 85,
    first_name: 'Brandon', last_name: 'Clark', email: 'brandon.clark@email.com', address: '258 Desert View', postal_code: '84661',
    phone_number: '4355558888', birth_date: '1991-08-09', gender: 'Male', emergency_contact_name: 'Michelle Clark',
    emergency_contact_phone: '4355559999', emergency_contact_relation: 'Wife', age_verification: true,
    interesting_fact: 'Early bird who loves morning competitions', full_signature: 'Brandon Clark', waiver_accepted: true,
    payment_status: 'pending', confirmation_number: '7890123', created_at: '2026-01-21T08:45:00Z'
  },
  {
    id: '9', event_id: '27', event_name: 'Barrel Racing', event_type: 'Slack Rodeo 7AM', price: 85,
    first_name: 'Ashley', last_name: 'Davis', email: 'ashley.davis@email.com', address: '369 Mountain View', postal_code: '84662',
    phone_number: '4355550000', birth_date: '1996-01-28', gender: 'Female', emergency_contact_name: 'Kevin Davis',
    emergency_contact_phone: '4355551111', emergency_contact_relation: 'Brother', age_verification: true,
    interesting_fact: 'Prefers early morning runs with her horse', full_signature: 'Ashley Davis', waiver_accepted: true,
    payment_status: 'pending', confirmation_number: '8901234', created_at: '2026-01-22T07:15:00Z'
  }
];

type AdminTab = 'registrations' | 'lineup';

const AdminView = () => {
  const [activeTab, setActiveTab] = useState<AdminTab>('registrations');
  const [contestants, setContestants] = useState<ContestantData[]>([]);
  const [allEvents, setAllEvents] = useState<EventAvailability[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [contestantToDelete, setContestantToDelete] = useState<ContestantData | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [waiverFilter, setWaiverFilter] = useState<'all' | 'waiver_required'>('all');

  // Lineup state
  const [lineupContestants, setLineupContestants] = useState<LineupContestant[]>([]);
  const [lineupEventFilter, setLineupEventFilter] = useState<string>('');
  const [lineupLoading, setLineupLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load both contestants and all events
      await Promise.all([loadContestants(), loadAllEvents()]);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadContestants = async () => {
    try {
      if (!supabase) {
        // Use mock data when Supabase is not available
        console.log('Using mock contestant data - Supabase not available');
        setContestants(mockContestantData);
        setIsOfflineMode(true);
        return;
      }

      // Fetch contestants with event information using a proper JOIN
      const { data, error } = await supabase
        .from('contestant_registrations')
        .select(`
          *,
          events!inner (
            event_name,
            event_type,
            price
          )
        `)
        .gte('created_at', '2026-06-01T00:00:00.000Z')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching contestants:', error);
        // Fall back to mock data
        setContestants(mockContestantData);
        setIsOfflineMode(true);
      } else {
        // Transform the data to match our interface
        const transformedData = data?.map(item => ({
          ...item,
          event_name: item.events?.event_name || 'Unknown Event',
          event_type: item.events?.event_type || 'Unknown Type',
          price: item.events?.price || 0
        })) || [];
        
        console.log('Loaded contestants from database:', transformedData);
        setContestants(transformedData);
        setIsOfflineMode(false);
      }
    } catch (err) {
      console.error('Error loading contestants:', err);
      setContestants(mockContestantData);
      setIsOfflineMode(true);
    }
  };

  const loadAllEvents = async () => {
    try {
      const eventsData = await getAvailableEvents();
      console.log('Loaded all events:', eventsData);
      setAllEvents(eventsData);
    } catch (error) {
      console.error('Error loading events:', error);
      setIsOfflineMode(true);
    }
  };

  // Lineup data loading
  const fetchLineup = useCallback(async () => {
    setLineupLoading(true);
    const data = await getLineup(lineupEventFilter || undefined);
    setLineupContestants(data);
    setLineupLoading(false);
  }, [lineupEventFilter]);

  useEffect(() => {
    if (activeTab === 'lineup') {
      fetchLineup();
    }
  }, [activeTab, fetchLineup]);

  useEffect(() => {
    if (activeTab !== 'lineup') return;
    const sub = subscribeToLineup(lineupEventFilter || undefined, fetchLineup);
    return () => sub.unsubscribe();
  }, [activeTab, lineupEventFilter, fetchLineup]);

  const handleDeleteClick = (contestant: ContestantData) => {
    setContestantToDelete(contestant);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contestantToDelete) return;

    setDeleting(true);

    try {
      if (!supabase || isOfflineMode) {
        // For offline mode, just remove from local state
        setContestants(prev => prev.filter(c => c.id !== contestantToDelete.id));
        console.log('Offline mode: Removed contestant from local state');
      } else {
        // Delete from Supabase
        console.log('Attempting to delete contestant:', contestantToDelete.id);
        
        const { error } = await supabase
          .from('contestant_registrations')
          .delete()
          .eq('id', contestantToDelete.id);

        if (error) {
          console.error('Error deleting contestant:', error);
          alert(`Failed to delete contestant: ${error.message}`);
          return;
        }

        console.log('Successfully deleted contestant from database:', contestantToDelete.id);
        
        // If successful, remove from local state
        setContestants(prev => prev.filter(c => c.id !== contestantToDelete.id));
      }

      // Close modal and reset state
      setDeleteModalOpen(false);
      setContestantToDelete(null);
    } catch (error) {
      console.error('Unexpected error deleting contestant:', error);
      alert('An unexpected error occurred while deleting the contestant. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setContestantToDelete(null);
  };

  // FIXED: Group ALL events by type, not just those with contestants
  // This ensures every event shows up in the admin view, even with 0 registrations
  const groupedEventsByType = allEvents.reduce((acc, event) => {
    if (!acc[event.event_type]) {
      acc[event.event_type] = {};
    }
    
    // Initialize each event with empty contestants array
    if (!acc[event.event_type][event.event_name]) {
      acc[event.event_type][event.event_name] = {
        event: event,
        contestants: []
      };
    }
    
    return acc;
  }, {} as Record<string, Record<string, { event: EventAvailability; contestants: ContestantData[] }>>);

  // Apply waiver filter
  const filteredContestants = waiverFilter === 'waiver_required'
    ? contestants.filter(c => c.waiver_required)
    : contestants;

  // Now add contestants to their respective events
  filteredContestants.forEach(contestant => {
    const { event_type, event_name } = contestant;
    
    if (groupedEventsByType[event_type] && groupedEventsByType[event_type][event_name]) {
      groupedEventsByType[event_type][event_name].contestants.push(contestant);
    } else {
      // Log any contestants without matching events for debugging
      console.warn('Contestant without matching event:', {
        contestant_name: `${contestant.first_name} ${contestant.last_name}`,
        event_type: contestant.event_type,
        event_name: contestant.event_name,
        event_id: contestant.event_id
      });
    }
  });

  console.log('Final grouped events by type:', groupedEventsByType);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status as keyof typeof styles] || styles.pending}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const toggleEventExpansion = (eventKey: string) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventKey)) {
      newExpanded.delete(eventKey);
    } else {
      newExpanded.add(eventKey);
    }
    setExpandedEvents(newExpanded);
  };

  const downloadCSV = () => {
    // Prepare CSV headers
    const headers = [
      'ID', 'Event Name', 'Event Type', 'Entry Fee', 'Registration Date',
      'First Name', 'Last Name', 'Email', 'Phone', 'Address', 'Postal Code',
      'Birth Date', 'Gender', 'Emergency Contact', 'Emergency Phone', 'Emergency Relation',
      'Age Verified', 'Waiver Required', 'Interesting Fact', 'Signature', 'Waiver Accepted', 'Payment Status',
      'Confirmation Number', 'Is Team Event', 'Partner Name', 'Partner Email', 'Partner Phone'
    ];

    // Convert data to CSV format
    const csvData = contestants.map(contestant => [
      contestant.id,
      contestant.event_name,
      contestant.event_type,
      contestant.price,
      formatDate(contestant.created_at),
      contestant.first_name,
      contestant.last_name,
      contestant.email,
      contestant.phone_number,
      contestant.address,
      contestant.postal_code,
      contestant.birth_date,
      contestant.gender,
      contestant.emergency_contact_name,
      contestant.emergency_contact_phone,
      contestant.emergency_contact_relation,
      contestant.age_verification ? 'Yes' : 'No',
      contestant.waiver_required ? 'Yes' : 'No',
      contestant.interesting_fact,
      contestant.full_signature,
      contestant.waiver_accepted ? 'Yes' : 'No',
      contestant.payment_status,
      contestant.confirmation_number || '',
      contestant.is_team_event ? 'Yes' : 'No',
      contestant.partner_name || '',
      contestant.partner_email || '',
      contestant.partner_phone || ''
    ]);

    // Combine headers and data
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `rodeo-registrations-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto pt-24">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Loading contestant data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto pt-32">
      <div className="mb-8">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary-900 mb-4 flex items-center">
              <Shield className="mr-3" size={32} />
              Admin Dashboard - Contestant Registrations
            </h1>
            
            {isOfflineMode && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <FileText className="text-yellow-600 mr-2" size={20} />
                  <div>
                    <h3 className="text-yellow-800 font-semibold">Demo Mode Active</h3>
                    <p className="text-yellow-700 text-sm mt-1">
                      Database connection unavailable. Showing sample contestant data for demonstration.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* CSV Download Button */}
            <Button
              variant="secondary"
              onClick={downloadCSV}
              icon={<Download size={18} />}
              className="shrink-0"
            >
              Download CSV
            </Button>
          </div>
        </div>

        {/* Event Availability Button */}
        <div className="mb-6 flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowAvailabilityModal(true)}
            icon={<Info size={18} />}
          >
            Show Event Availability
          </Button>

          <Button
            variant={waiverFilter === 'waiver_required' ? 'primary' : 'outline'}
            onClick={() => {
              if (waiverFilter === 'all') {
                setWaiverFilter('waiver_required');
                // Auto-expand all events that have waiver-required contestants
                const waiverContestants = contestants.filter(c => c.waiver_required);
                const keysToExpand = new Set<string>();
                waiverContestants.forEach(c => {
                  keysToExpand.add(`${c.event_type}-${c.event_name}`);
                });
                setExpandedEvents(keysToExpand);
              } else {
                setWaiverFilter('all');
              }
            }}
            icon={<AlertTriangle size={18} />}
          >
            {waiverFilter === 'waiver_required' ? 'Showing Waiver Required Only' : 'Filter: Waiver Required'}
          </Button>

          {waiverFilter === 'waiver_required' && (
            <span className="text-sm text-amber-700 font-medium">
              {contestants.filter(c => c.waiver_required).length} contestant{contestants.filter(c => c.waiver_required).length !== 1 ? 's' : ''} need waiver
            </span>
          )}
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <Users className="text-primary-600" size={20} />
              <div>
                <p className="text-2xl font-bold text-primary-900">{contestants.length}</p>
                <p className="text-sm text-gray-600">Total Registrations</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center space-x-2">
              <Calendar className="text-green-600" size={20} />
              <div>
                <p className="text-2xl font-bold text-green-900">{Object.keys(groupedEventsByType).length}</p>
                <p className="text-sm text-gray-600">Event Days</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center space-x-2">
              <DollarSign className="text-blue-600" size={20} />
              <div>
                <p className="text-2xl font-bold text-blue-900">
                  ${contestants.reduce((sum, c) => sum + c.price, 0).toLocaleString()}
                </p>
                <p className="text-sm text-gray-600">Total Entry Fees</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center space-x-2">
              <Shield className="text-purple-600" size={20} />
              <div>
                <p className="text-2xl font-bold text-purple-900">
                  {contestants.filter(c => c.payment_status === 'paid').length}
                </p>
                <p className="text-sm text-gray-600">Paid Entries</p>
              </div>
            </div>

            <div className="flex items-center justify-center space-x-2">
              <AlertTriangle className="text-amber-600" size={20} />
              <div>
                <p className="text-2xl font-bold text-amber-900">
                  {contestants.filter(c => c.waiver_required).length}
                </p>
                <p className="text-sm text-gray-600">Waiver Required</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('registrations')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'registrations'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users size={16} className="inline mr-2" />
            Registrations
          </button>
          <button
            onClick={() => setActiveTab('lineup')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'lineup'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <List size={16} className="inline mr-2" />
            Competition Lineup
          </button>
        </nav>
      </div>

      {/* Lineup Tab */}
      {activeTab === 'lineup' && (
        <div>
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-4">
              The most recent registrant competes first (position 1). The first to register competes last.
            </p>
            <div className="max-w-xs">
              <Select
                label="Filter by Event"
                id="lineup-event-filter"
                name="lineup-event-filter"
                value={lineupEventFilter}
                onChange={(e) => setLineupEventFilter(e.target.value)}
                options={[
                  { value: '', label: 'All Events' },
                  ...allEvents.map(ev => ({ value: ev.id, label: `${ev.event_name} — ${ev.event_type}` })),
                ]}
              />
            </div>
          </div>

          {lineupLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : lineupContestants.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-xl">
              <Users size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No contestants registered yet.</p>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="grid grid-cols-12 gap-3 px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <div className="col-span-1 text-center">#</div>
                <div className="col-span-4">Contestant</div>
                <div className="col-span-4">Event</div>
                <div className="col-span-3 text-right">Registered</div>
              </div>

              <AnimatePresence mode="popLayout">
                {lineupContestants.map((contestant, index) => {
                  const position = index + 1;
                  const registeredDate = new Date(contestant.registered_at);
                  const timeStr = registeredDate.toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  });

                  return (
                    <motion.div
                      key={contestant.id}
                      layout
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.3, delay: index * 0.03 }}
                      className="grid grid-cols-12 gap-3 items-center px-4 py-3 bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="col-span-1 text-center">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                          position === 1
                            ? 'bg-amber-100 text-amber-800'
                            : position === 2
                            ? 'bg-gray-100 text-gray-700'
                            : position === 3
                            ? 'bg-orange-50 text-orange-700'
                            : 'bg-gray-50 text-gray-600'
                        }`}>
                          {position}
                        </span>
                      </div>

                      <div className="col-span-4">
                        <p className="font-medium text-gray-900">
                          {contestant.first_name} {contestant.last_name}
                        </p>
                      </div>

                      <div className="col-span-4">
                        <p className="text-sm text-gray-700">{contestant.event_name}</p>
                        <p className="text-xs text-gray-500">{contestant.event_type}</p>
                      </div>

                      <div className="col-span-3 text-right">
                        <span className="inline-flex items-center text-xs text-gray-500">
                          <Clock size={12} className="mr-1" />
                          {timeStr}
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>

              <div className="mt-6 text-center text-sm text-gray-500">
                Showing {lineupContestants.length} contestant{lineupContestants.length !== 1 ? 's' : ''} — position 1 competes first
              </div>
            </div>
          )}
        </div>
      )}

      {/* Registrations Tab */}
      {activeTab === 'registrations' && <>
      {/* Debug information */}
      {isOfflineMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-blue-800 mb-2">Debug Information</h4>
          <p className="text-blue-700 text-sm">
            Total events loaded: {allEvents.length} | 
            Total contestants loaded: {contestants.length} | 
            Event types: {Object.keys(groupedEventsByType).join(', ')}
          </p>
        </div>
      )}

      {Object.entries(groupedEventsByType).map(([eventType, events]) => (
        <motion.div
          key={eventType}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-heading font-bold text-primary-800 mb-6 flex items-center">
            <Calendar className="mr-2" size={24} />
            {eventType}
            <span className="ml-3 px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm font-medium">
              {Object.values(events).reduce((sum, eventData) => sum + eventData.contestants.length, 0)} registrations
            </span>
          </h2>

          {Object.entries(events).map(([eventName, eventData]) => {
            const eventKey = `${eventType}-${eventName}`;
            const isExpanded = expandedEvents.has(eventKey);
            const eventContestants = eventData.contestants;
            const event = eventData.event;

            return (
              <div key={eventName} className="mb-4 bg-white rounded-lg shadow-sm border overflow-hidden">
                <div 
                  className="bg-gray-50 px-6 py-4 border-b cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleEventExpansion(eventKey)}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                      <h3 className="text-lg font-semibold text-gray-900 ml-2">{eventName}</h3>
                      {eventContestants.length === 0 && (
                        <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                          NO REGISTRATIONS
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span className="flex items-center">
                        <Users size={16} className="mr-1" />
                        {eventContestants.length} contestants
                      </span>
                      <span className="flex items-center">
                        <DollarSign size={16} className="mr-1" />
                        ${event.price} entry fee
                      </span>
                      <span className="text-xs text-gray-500">
                        {event.available_spots === 999999 ? 'Unlimited' : `${event.available_spots} spots left`}
                      </span>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {eventContestants.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <Users className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                        <p className="text-lg font-medium">No registrations yet</p>
                        <p className="text-sm">This event is available for registration but no one has signed up yet.</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Contestant
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Contact
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Location
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Emergency Contact
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Registration
                              </th>
                              {eventContestants.some(c => c.is_team_event) && (
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Team Partner
                                </th>
                              )}
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {eventContestants.map((contestant) => (
                              <tr key={contestant.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10">
                                      <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                        <User className="h-5 w-5 text-primary-600" />
                                      </div>
                                    </div>
                                    <div className="ml-4">
                                      <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                        {contestant.first_name} {contestant.last_name}
                                        {contestant.waiver_required && (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                                            <AlertTriangle size={12} className="mr-1" />
                                            WAIVER REQUIRED
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        {contestant.gender} • Age verified: {contestant.age_verification ? '\u2713' : '\u2717'}
                                      </div>
                                      <div className="text-xs text-gray-400 mt-1">
                                        {contestant.interesting_fact}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900 flex items-center">
                                    <Mail className="h-4 w-4 mr-1 text-gray-400" />
                                    {contestant.email}
                                  </div>
                                  <div className="text-sm text-gray-500 flex items-center">
                                    <Phone className="h-4 w-4 mr-1 text-gray-400" />
                                    {contestant.phone_number}
                                  </div>
                                </td>
                                
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900 flex items-center">
                                    <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                                    {contestant.address}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {contestant.postal_code}
                                  </div>
                                </td>
                                
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">
                                    {contestant.emergency_contact_name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {contestant.emergency_contact_phone}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {contestant.emergency_contact_relation}
                                  </div>
                                </td>
                                
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center space-x-2">
                                    {getStatusBadge(contestant.payment_status)}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1 flex items-center">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {formatDate(contestant.created_at)}
                                  </div>
                                  {contestant.confirmation_number && (
                                    <div className="text-xs text-gray-400 mt-1">
                                      #{contestant.confirmation_number}
                                    </div>
                                  )}
                                </td>
                                
                                {eventContestants.some(c => c.is_team_event) && (
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {contestant.is_team_event && contestant.partner_name ? (
                                      <div>
                                        <div className="text-sm text-gray-900">{contestant.partner_name}</div>
                                        <div className="text-sm text-gray-500">{contestant.partner_email}</div>
                                        <div className="text-xs text-gray-400">{contestant.partner_phone}</div>
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">—</span>
                                    )}
                                  </td>
                                )}

                                <td className="px-6 py-4 whitespace-nowrap">
                                  <button
                                    onClick={() => handleDeleteClick(contestant)}
                                    className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-50 transition-colors"
                                    title="Delete registration"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            );
          })}
        </motion.div>
      ))}

      {contestants.length === 0 && allEvents.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No data available</h3>
          <p className="mt-1 text-sm text-gray-500">
            No events or registrations found. Please check your database connection.
          </p>
        </div>
      )}

      </>}

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={handleDeleteCancel}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            >
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Delete Registration
                  </h3>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-2">
                  Are you sure you want to delete the registration for:
                </p>
                <p className="font-medium text-gray-900">
                  {contestantToDelete?.first_name} {contestantToDelete?.last_name}
                </p>
                <p className="text-sm text-gray-500">
                  {contestantToDelete?.event_name} - {contestantToDelete?.event_type}
                </p>
                <p className="text-sm text-red-600 mt-2">
                  This action cannot be undone.
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={handleDeleteCancel}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="accent"
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                  icon={deleting ? undefined : <Trash2 size={16} />}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Event Availability Modal */}
      <EventAvailabilityModal
        isOpen={showAvailabilityModal}
        onClose={() => setShowAvailabilityModal(false)}
      />
    </div>
  );
};

export default AdminView;